import type { CustomDimensions } from '@de/types/types';
import { interpolate } from '@de/utils/interpolation';
import {
  parseCustomDimensionMapping,
  parseFieldSource,
  type CustomDimensionMapping,
  type FieldSource,
  type FieldSourceParseResult,
} from './fieldSource';

export const ELEMENT_EVENT_FIELD_KEYS = [
  'elementType',
  'name',
  'htmlId',
  'location',
  'text',
] as const;

export type ElementEventFieldKey = (typeof ELEMENT_EVENT_FIELD_KEYS)[number];
export type ElementFieldMappings = Partial<
  Record<ElementEventFieldKey, FieldSource>
>;

/**
 * Session + document metadata copied onto `host.page`.
 * `tenantId` is host-visible and never author-mappable.
 */
export const ELEMENT_HOST_PAGE_KEYS = [
  'tenantId',
  'dashboardConfigId',
  'blockId',
  'blockType',
  'blockLabel',
  'sectionSlug',
  'subTabSlug',
  'sectionLabel',
  'subTabLabel',
  'modelId',
  'metricNames',
  'dimensionNames',
  'chartType',
  'filterFormat',
] as const;

export type ElementHostPageKey = (typeof ELEMENT_HOST_PAGE_KEYS)[number];

/**
 * Author-mappable data paths. Sensitive roots (`tenantId`,
 * `formState`, `flowContext`) stay off this list.
 */
export const ELEMENT_ALLOWED_DATA_PATHS = [
  'elementType',
  'name',
  'htmlId',
  'location',
  'text',
  'dashboardConfigId',
  'blockId',
  'blockType',
  'blockLabel',
  'sectionSlug',
  'subTabSlug',
  'modelId',
  'metricNames',
  'dimensionNames',
  'chartType',
  'filterFormat',
] as const;

export type ElementAllowedDataPath =
  (typeof ELEMENT_ALLOWED_DATA_PATHS)[number];

export const ELEMENT_CUSTOM_DIMENSION_KEYS = [
  'sectionSlug',
  'subTabSlug',
  'modelId',
  'metricNames',
  'dimensionNames',
  'blockId',
  'blockType',
  'blockLabel',
  'dashboardConfigId',
  'chartType',
  'filterFormat',
] as const;

export type ElementCustomDimensionKey =
  (typeof ELEMENT_CUSTOM_DIMENSION_KEYS)[number];

export type ElementFieldDiagnosticReason =
  | 'invalid-source'
  | 'unknown-field'
  | 'disallowed-path'
  | 'sensitive-path'
  | 'unresolved';

export type HostElementTrackingSlice = {
  readonly elementType?: string;
  readonly name?: string;
  readonly htmlId?: string;
  readonly location?: string;
  readonly text?: string;
  readonly fields?: Readonly<Record<string, unknown>>;
  readonly customDimensions?: unknown;
};

const ELEMENT_EVENT_FIELD_KEY_SET: ReadonlySet<string> = new Set(
  ELEMENT_EVENT_FIELD_KEYS,
);
const ELEMENT_ALLOWED_DATA_PATH_SET: ReadonlySet<string> = new Set(
  ELEMENT_ALLOWED_DATA_PATHS,
);
const ELEMENT_CUSTOM_DIMENSION_KEY_SET: ReadonlySet<string> = new Set(
  ELEMENT_CUSTOM_DIMENSION_KEYS,
);
const SENSITIVE_PATH_SEGMENTS = new Set([
  '__proto__',
  'constructor',
  'prototype',
  'email',
  'phone',
  'chat',
  'insight',
  'metric',
  'row',
  'rows',
  'result',
  'results',
  'export',
  'user',
  'tenantid',
  'formstate',
  'flowcontext',
]);

/**
 * Metadata-only resolve root for `host.page`.
 *
 * Copies scalar host keys only. Nested records, rows, Product
 * identity, and other sensitive values stay off the public tracking host.
 */
export function createHostTrackingPage(
  page: Readonly<Record<string, unknown>>,
): Readonly<Record<string, unknown>> {
  const hostPage: Record<string, unknown> = {};
  for (const key of ELEMENT_HOST_PAGE_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(page, key)) {
      continue;
    }
    const value = page[key];
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      hostPage[key] = value;
    }
  }
  return hostPage;
}

/**
 * Resolve authored Element fields against an approved metadata context.
 */
export function resolveElementFields(
  fields: Readonly<Record<string, FieldSource>> | undefined,
  context: Readonly<Record<string, unknown>>,
  defaults: Readonly<Record<string, string>> = {},
): Readonly<Record<string, string>> {
  if (!fields || Object.keys(fields).length === 0) {
    return defaults;
  }

  const resolved: Record<string, string> = { ...defaults };
  for (const [fieldKey, source] of Object.entries(fields)) {
    if (!ELEMENT_EVENT_FIELD_KEY_SET.has(fieldKey)) {
      warn(fieldKey, 'unknown-field');
      continue;
    }

    const value = resolveSource(fieldKey, source, context);
    if (value !== undefined) {
      resolved[fieldKey] = value;
    }
  }
  return resolved;
}

/**
 * Normalize `customDimensions` authored as an array or object map
 * into the shared mapping array.
 */
export function toElementCustomDimensionMappings(
  raw: unknown,
): CustomDimensionMapping[] | undefined {
  if (raw === undefined || raw === null) {
    return undefined;
  }
  if (Array.isArray(raw)) {
    return raw as CustomDimensionMapping[];
  }
  if (!isPlainObject(raw)) {
    warn('customDimension', 'invalid-source');
    return [];
  }

  const mappings: CustomDimensionMapping[] = [];
  for (const [key, source] of Object.entries(raw)) {
    if (!ELEMENT_CUSTOM_DIMENSION_KEY_SET.has(key)) {
      warn(key, 'unknown-field');
      continue;
    }
    if (typeof source === 'string') {
      mappings.push({ key, dataPath: source });
      continue;
    }
    if (!isPlainObject(source)) {
      warn(key, 'invalid-source');
      continue;
    }
    const hasDataPath = Object.prototype.hasOwnProperty.call(
      source,
      'dataPath',
    );
    const hasLiteral = Object.prototype.hasOwnProperty.call(source, 'literal');
    if (hasDataPath && !hasLiteral && typeof source.dataPath === 'string') {
      mappings.push({ key, dataPath: source.dataPath });
      continue;
    }
    if (hasLiteral && !hasDataPath && typeof source.literal === 'string') {
      mappings.push({ key, literal: source.literal });
      continue;
    }
    warn(key, 'invalid-source');
  }
  return mappings;
}

/**
 * Resolve sibling custom-dimension mappings through the Element allowlist.
 */
export function resolveElementCustomDimensions(
  mappings: readonly CustomDimensionMapping[] | undefined,
  context: Readonly<Record<string, unknown>>,
): CustomDimensions {
  if (!mappings || mappings.length === 0) {
    return [];
  }

  const resolved: CustomDimensions = [];
  for (const mapping of mappings) {
    const parsed = parseCustomDimensionMapping(mapping);
    if (parsed.kind === 'empty') {
      continue;
    }
    if (parsed.kind === 'invalid') {
      warn('customDimension', 'invalid-source');
      continue;
    }
    if (!ELEMENT_CUSTOM_DIMENSION_KEY_SET.has(parsed.key)) {
      warn(parsed.key, 'unknown-field');
      continue;
    }

    const value = resolveParsedSource(parsed.key, parsed, context);
    if (value !== undefined) {
      resolved.push({ key: parsed.key, value });
    }
  }
  return resolved;
}

function isEmptyDimensionMap(value: unknown): boolean {
  if (Array.isArray(value)) return value.length === 0;
  return isPlainObject(value) && Object.keys(value).length === 0;
}

/**
 * Missing map uses the host registry. An empty authored map emits
 * nothing. An authored map replaces the registry. Only keys in the
 * effective map are copied from the block context.
 */
export function resolveHostCustomDimensions(
  tracking: HostElementTrackingSlice,
  hostPage: Readonly<Record<string, unknown>>,
  registryCustomDimensions?: unknown,
): CustomDimensions {
  const source =
    tracking.customDimensions !== undefined
      ? tracking.customDimensions
      : registryCustomDimensions;
  if (source === undefined || source === null || isEmptyDimensionMap(source)) {
    return [];
  }
  return resolveElementCustomDimensions(
    toElementCustomDimensionMappings(source),
    hostPage,
  );
}

/**
 * Merge authored identity, field maps, and custom dimensions into the
 * element trigger override. Lifecycle events and host signals share
 * this function.
 */
export function buildHostElementEventOverride(
  tracking: HostElementTrackingSlice,
  hostPage: Readonly<Record<string, unknown>>,
  registryCustomDimensions?: unknown,
): Record<string, unknown> {
  const defaults: Record<string, string> = {};
  for (const key of ELEMENT_EVENT_FIELD_KEYS) {
    const value = tracking[key];
    if (typeof value === 'string' && value.length > 0) {
      defaults[key] = value;
    }
  }

  const resolvedFields = resolveElementFields(
    tracking.fields as ElementFieldMappings | undefined,
    hostPage,
    defaults,
  );
  const customDimensions = resolveHostCustomDimensions(
    tracking,
    hostPage,
    registryCustomDimensions,
  );

  const override: Record<string, unknown> = { ...resolvedFields };
  if (customDimensions.length > 0) {
    override.customDimensions = customDimensions;
  }
  return override;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function resolveSource(
  fieldKey: string,
  source: FieldSource,
  context: Readonly<Record<string, unknown>>,
): string | undefined {
  const parsed = parseFieldSource(source);
  if (parsed.kind === 'empty') {
    return undefined;
  }
  if (parsed.kind === 'invalid') {
    warn(fieldKey, 'invalid-source');
    return undefined;
  }
  return resolveParsedSource(fieldKey, parsed, context);
}

function resolveParsedSource(
  fieldKey: string,
  parsed: Extract<FieldSourceParseResult, { kind: 'dataPath' | 'literal' }>,
  context: Readonly<Record<string, unknown>>,
): string | undefined {
  if (parsed.kind === 'literal') {
    return parsed.literal;
  }

  const pathReason = getPathRejectionReason(parsed.dataPath);
  if (pathReason) {
    warn(fieldKey, pathReason);
    return undefined;
  }

  let value: unknown;
  try {
    value = interpolate(`{{${parsed.dataPath}}}`, context);
  } catch {
    warn(fieldKey, 'unresolved');
    return undefined;
  }

  const coerced = coerceTrackingScalar(value);
  if (coerced === undefined || coerced.length === 0) {
    warn(fieldKey, 'unresolved');
    return undefined;
  }
  return coerced;
}

function getPathRejectionReason(
  dataPath: string,
): 'sensitive-path' | 'disallowed-path' | undefined {
  const segments = dataPath
    .toLowerCase()
    .split(/[.[\]]+/)
    .filter(Boolean);
  if (segments.some((segment) => SENSITIVE_PATH_SEGMENTS.has(segment))) {
    return 'sensitive-path';
  }
  if (!ELEMENT_ALLOWED_DATA_PATH_SET.has(dataPath)) {
    return 'disallowed-path';
  }
  return undefined;
}

/**
 * String form of a scalar. Arrays of scalars join with commas.
 * Objects and other values are omitted.
 */
export function coerceTrackingScalar(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value
      .map((item) =>
        typeof item === 'string' ||
        typeof item === 'number' ||
        typeof item === 'boolean'
          ? String(item)
          : '',
      )
      .filter(Boolean)
      .join(',');
  }
  return undefined;
}

function warn(fieldKey: string, reason: ElementFieldDiagnosticReason): void {
  // eslint-disable-next-line no-console
  console.warn(`[tracking] element-field-resolve: ${reason} field=${fieldKey}`);
}
